/**
 * Đo tốc độ mạng và ước thời gian còn lại cho các hộp tải ảnh.
 *
 * Hai cách đo, theo thứ mà đường gửi báo về được:
 * - Thẻ "Sửa ảnh" của đơn gửi bằng `fetch`, mà `fetch` không báo tiến độ gửi,
 *   nên đo theo TỪNG TẤM đã xong ({@link uploadSpeed}, {@link uploadEta}). Mẻ
 *   vài trăm tấm thì nhịp một tấm là đủ mịn.
 * - Hộp tải album báo tiến độ từng byte, nên đo theo byte trong một cửa sổ
 *   thời gian ({@link ByteRateMeter}).
 *
 * Cả hai chỉ nhìn đoạn gần nhất: mạng quán hay lên xuống, lấy trung bình cả mẻ
 * thì con số trễ hàng chục phút mới theo kịp.
 */

export const WINDOW = 8;

export type UploadSample = {
  /** Dung lượng tệp người dùng chọn (trước nén). */
  origBytes: number;
  /** Dung lượng thật đã gửi lên máy chủ (sau nén). */
  sentBytes: number;
  /** Thời gian gửi riêng, mili giây. */
  sendMs: number;
  /** Cả nén lẫn gửi, mili giây. */
  totalMs: number;
};

/** Tốc độ gửi, byte/giây. Chưa có tấm nào xong thì `null`. */
export function uploadSpeed(samples: UploadSample[]): number | null {
  const recent = samples.slice(-WINDOW);
  const bytes = recent.reduce((s, x) => s + x.sentBytes, 0);
  const ms = recent.reduce((s, x) => s + x.sendMs, 0);
  if (recent.length === 0 || ms <= 0) return null;
  return (bytes / ms) * 1000;
}

/**
 * Số giây còn lại. Tính theo DUNG LƯỢNG còn lại chứ không theo số tấm: một mẻ
 * lẫn ảnh 3 MB với ảnh 40 MB thì đếm tấm là nói dối. Thời gian lấy cả bước
 * nén, vì máy yếu nén còn lâu hơn gửi.
 */
export function uploadEta(
  samples: UploadSample[],
  remainingBytes: number,
  remainingFiles: number,
): number | null {
  const recent = samples.slice(-WINDOW);
  if (recent.length === 0) return null;
  if (remainingFiles <= 0) return 0;
  const ms = recent.reduce((s, x) => s + x.totalMs, 0);
  const bytes = recent.reduce((s, x) => s + x.origBytes, 0);
  const seconds = bytes > 0 ? (ms / bytes) * remainingBytes / 1000 : (ms / recent.length) * remainingFiles / 1000;
  return Number.isFinite(seconds) ? Math.max(0, seconds) : null;
}

/** 3_400_000 → "3,2 MB/giây". */
export function formatSpeed(bytesPerSec: number): string {
  const kb = bytesPerSec / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB/giây`;
  return `${(kb / 1024).toLocaleString("vi-VN", { maximumFractionDigits: 1 })} MB/giây`;
}

/** 1500 → "còn khoảng 25 phút". */
export function formatEta(seconds: number): string {
  if (seconds < 60) return "còn dưới 1 phút";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `còn khoảng ${minutes} phút`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `còn khoảng ${h} giờ ${m} phút` : `còn khoảng ${h} giờ`;
}

/**
 * Tốc độ theo byte trong {@link ByteRateMeter.windowMs} mili giây gần nhất.
 *
 * Gọi {@link push} mỗi khi tổng byte đã gửi đổi; đọc {@link speed}. Tổng tụt
 * xuống (bắt đầu lượt mới, thử lại) thì xoá sổ đo lại từ đầu, kẻo tốc độ âm.
 */
export class ByteRateMeter {
  private points: { t: number; bytes: number }[] = [];

  constructor(readonly windowMs = 10_000) {}

  push(bytes: number, now: number): void {
    const last = this.points[this.points.length - 1];
    if (last && bytes < last.bytes) this.points = [];
    this.points.push({ t: now, bytes });
    // Giữ lại một điểm ngay trước mép cửa sổ làm mốc đầu.
    while (this.points.length > 2 && now - this.points[1]!.t >= this.windowMs) this.points.shift();
  }

  /** Byte/giây; `null` khi chưa đủ một giây đo hoặc chưa nhích byte nào. */
  speed(): number | null {
    const first = this.points[0];
    const last = this.points[this.points.length - 1];
    if (!first || !last) return null;
    const ms = last.t - first.t;
    const bytes = last.bytes - first.bytes;
    if (ms < 1000 || bytes <= 0) return null;
    return (bytes / ms) * 1000;
  }

  reset(): void {
    this.points = [];
  }
}
